// Shopify is the source of truth for purchasable tickets. The local planning
// calculator is deliberately not imported by this module.
export function getTicketSelection(config, variantId, quantityValue) {
  const variant = config.variants.find(item => item.id === String(variantId));
  if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) throw new TypeError('This ticket option is unavailable. Please choose another option.');
  if (!variant.available) throw new TypeError('This ticket option is sold out. Please choose another option.');
  const minimum = Math.max(1, Number(variant.minimum) || 1);
  const increment = Math.max(1, Number(variant.increment) || 1);
  const maximum = Math.min(config.quantityCap, variant.maximum);
  const quantity = Number(quantityValue);
  if (!String(quantityValue).trim() || !Number.isSafeInteger(quantity) || quantity < minimum || quantity > maximum || (quantity - minimum) % increment !== 0) {
    const message = maximum < minimum
      ? 'There are not enough places left for this ticket option.'
      : `Enter a whole number of attendees from ${minimum} to ${maximum}${increment > 1 ? `, in steps of ${increment}` : ''}.`;
    throw new TypeError(message);
  }
  const total = variant.price * quantity;
  if (!Number.isSafeInteger(total)) throw new TypeError('Please choose fewer attendees.');
  return { variant, quantity, total, minimum, maximum, increment };
}

export function setupTicketCommerce(root, environment = {}) {
  if (!root?.dataset.mmCommerce) return;
  const hostWindow = environment.window || window;
  const request = environment.fetch || hostWindow.fetch.bind(hostWindow);
  const FormDataConstructor = environment.FormData || hostWindow.FormData;
  const select = selector => root.querySelector(selector);
  const form = select('.mm-product-cart-form');
  const quantityInput = select('#mm-product-quantity');
  const variantInput = select('[data-product-variant]');
  const mainButton = select('[data-product-review]');
  const finalButton = select('[data-product-buy]');
  const minus = select('[data-product-minus]');
  const plus = select('[data-product-plus]');
  const error = select('#mm-product-error');
  const recovery = select('[data-product-cart-recovery]');
  if (!form || !quantityInput || !variantInput || !mainButton || !error) return;
  // Associated form controls include the sticky checkout button outside the
  // product section, so it shares the same validation and submission lock.
  const checkoutButtons = Array.from(form.elements || []).filter(element => element.hasAttribute?.('data-product-checkout'));
  let config;
  try {
    config = JSON.parse(select('[data-product-commerce-config]').textContent);
    if (!Array.isArray(config.variants) || !config.variants.length) throw new Error('Missing variants');
  } catch {
    error.textContent = 'Ticket details could not be loaded. Please reload this page.';
    error.hidden = false;
    mainButton.disabled = true;
    if (finalButton) finalButton.disabled = true;
    checkoutButtons.forEach(button => { button.disabled = true; });
    return;
  }
  const salesReady = root.dataset.mmCommerce === 'ready' && config.enabled === true;
  const money = amount => new Intl.NumberFormat('en-GB', { style:'currency', currency:config.currency }).format(amount / 100);
  let busy = false;
  let currentSelection = null;

  function text(selector, value) {
    const element = select(selector);
    if (element) element.textContent = value;
  }

  function updatePassLinks() {
    const quantity = Number(quantityInput.value);
    const valid = quantityInput.value.trim() && Number.isInteger(quantity) && quantity >= 1 && quantity <= 100;
    root.querySelectorAll('[data-pass-link]').forEach(link => {
      const destination = new URL(link.getAttribute('href'), hostWindow.location.href);
      if (valid) destination.searchParams.set('attendees', String(quantity));
      else destination.searchParams.delete('attendees');
      link.setAttribute('href', `${destination.pathname}${destination.search}${destination.hash}`);
    });
  }

  function update({ clearError = true } = {}) {
    currentSelection = null;
    const variant = config.variants.find(item => item.id === String(variantInput.value));
    if (variant) {
      quantityInput.min = String(variant.minimum);
      quantityInput.max = String(Math.min(config.quantityCap, variant.maximum));
      quantityInput.step = String(variant.increment);
      text('[data-product-price]', money(variant.price));
      text('[data-product-action-price]', money(variant.price));
    }
    if (clearError) { error.hidden = true; recovery.hidden = true; }
    quantityInput.removeAttribute('aria-invalid');
    try {
      currentSelection = getTicketSelection(config, variantInput.value, quantityInput.value);
      const total = money(currentSelection.total);
      ['[data-product-total]', '[data-product-cta-total]', '[data-product-action-total]'].forEach(selector => text(selector, total));
      text('[data-product-action-quantity]', `${currentSelection.quantity} ${currentSelection.quantity === 1 ? 'attendee' : 'attendees'}`);
    } catch (issue) {
      if (clearError && salesReady) { error.textContent = issue.message; error.hidden = false; }
      quantityInput.setAttribute('aria-invalid', 'true');
      ['[data-product-total]', '[data-product-cta-total]', '[data-product-action-total]'].forEach(selector => text(selector, '—'));
      text('[data-product-action-quantity]', 'Check your selection');
    }
    const disabled = !salesReady || busy || !currentSelection;
    mainButton.disabled = disabled;
    if (finalButton) finalButton.disabled = disabled;
    checkoutButtons.forEach(button => {
      button.disabled = disabled;
      const label = button.querySelector('[data-product-checkout-label]');
      if (label) label.textContent = busy ? 'Adding tickets…' : !salesReady ? 'Tickets coming soon' : variant?.available ? 'Checkout' : 'Sold out';
    });
    quantityInput.disabled = !salesReady || busy;
    variantInput.disabled = !salesReady || busy;
    minus.disabled = !salesReady || busy || Number(quantityInput.value) <= Number(quantityInput.min);
    plus.disabled = !salesReady || busy || Number(quantityInput.value) >= Number(quantityInput.max);
    const label = busy ? 'Adding tickets…' : !salesReady ? 'Tickets coming soon' : variant?.available ? 'Get tickets' : 'Sold out';
    root.querySelectorAll('[data-product-buy-label]').forEach(element => { element.textContent = label; });
    updatePassLinks();
    return currentSelection;
  }

  function changeQuantity(direction) {
    if (busy || !salesReady) return;
    const value = Number(quantityInput.value);
    const minimum = Number(quantityInput.min);
    const increment = Number(quantityInput.step);
    const maximum = Number(quantityInput.max);
    const offset = (value - minimum) / increment;
    const next = Number.isFinite(value)
      ? minimum + (direction > 0 ? Math.floor(offset) + 1 : Math.ceil(offset) - 1) * increment
      : minimum;
    const highest = minimum + Math.floor((maximum - minimum) / increment) * increment;
    quantityInput.value = String(Math.max(minimum, Math.min(highest, next)));
    update();
  }

  const incomingQuantity = new URLSearchParams(hostWindow.location.search).get('attendees');
  if (incomingQuantity !== null && /^\d{1,3}$/.test(incomingQuantity)) quantityInput.value = incomingQuantity;
  quantityInput.addEventListener('input', () => update());
  variantInput.addEventListener('change', () => {
    update();
    const destination = new URL(hostWindow.location.href);
    destination.searchParams.set('variant', variantInput.value);
    hostWindow.history.replaceState(null, '', destination.href);
  });
  minus.addEventListener('click', () => changeQuantity(-1));
  plus.addEventListener('click', () => changeQuantity(1));
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || !salesReady || !update()) return;
    const submittedSelection = currentSelection;
    const submitter = event.submitter?.form === form ? event.submitter : null;
    const goToCheckout = submitter?.hasAttribute('data-product-checkout') === true;
    // Capture enabled form fields before locking controls to prevent duplicate
    // submissions. Without JavaScript Shopify receives this same native form.
    const payload = new FormDataConstructor(form);
    // FormData(form) omits the clicked submit button. Preserve its native
    // name/value (including the no-JavaScript checkout return path).
    if (submitter?.name && !submitter.disabled) payload.append(submitter.name, submitter.value);
    busy = true;
    form.setAttribute('aria-busy', 'true');
    update();
    let added = false;
    let cartError = '';
    try {
      const response = await request(config.cartAddUrl, {
        method:'POST', headers:{ Accept:'application/json' }, body:payload, credentials:'same-origin',
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) {
        cartError = typeof result.description === 'string' ? result.description : 'These tickets could not be added. Please check your basket and try again.';
        throw new Error('Cart request rejected');
      }
      added = true;
      // Analytics must never delay or break a successful basket update.
      const track = (name, properties) => {
        try { hostWindow.mmAnalytics?.capture(name, properties); }
        catch { /* Tracking must not prevent checkout or basket navigation. */ }
      };
      track('ticket_added_to_cart', {
        ticket_type: root.dataset.ticketId,
        variant_id: submittedSelection.variant.id,
        quantity: submittedSelection.quantity,
        value: submittedSelection.total / 100,
        currency: config.currency,
      });
      if (goToCheckout) track('checkout_clicked');
      hostWindow.location.assign(goToCheckout ? config.checkoutUrl : config.cartUrl);
    } catch {
      error.textContent = cartError || 'We could not confirm whether the tickets were added. Check your basket before trying again.';
      error.hidden = false;
      recovery.hidden = false;
      error.setAttribute('tabindex', '-1');
      error.focus();
    } finally {
      if (!added) {
        busy = false;
        form.removeAttribute('aria-busy');
        update({ clearError:false });
      }
    }
  });
  update();
}

if (typeof document !== 'undefined') setupTicketCommerce(document.querySelector('[data-mm-product][data-mm-commerce]'));
